import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import classNames from 'classnames';
import { get } from 'lodash';

import PrimaryBarDropdown from 'apps/header/components/PrimaryBarDropdown';
import Locations from 'apps/basket/models/Locations';

import MiniBasket from '../containers/MiniBasket';
import BasketSummary from './BasketSummary';

import { links } from '[theme-lib]/links';

const Basket = ({
  basket, isBasketVisible, isDesktopBreakpoint, isLoggedIn, openTrolleyMenu, changeBasketQuantity,
}) => {
  useEffect(() => {
    const basketEventHandler = (action, e) => {
      if (!['addToBasket', 'removeFromBasket'].includes(action)) throw new Error('Action is not supported');

      const itemData = get(e, 'detail.itemData', {});
      const quantity = get(e, 'detail.quantity');
      const sign = action === 'addToBasket' ? 1 : -1;

      if (itemData.sku && quantity) {
        changeBasketQuantity({
          items: { [itemData.sku]: quantity * sign },
          location: Locations.EXTERNAL,
          analyticsContextData: itemData,
        });
      }
    };

    const addToBasketEventHandler = (e) => basketEventHandler('addToBasket', e);
    const removeFromBasketEventHandler = (e) => basketEventHandler('removeFromBasket', e);

    window.addEventListener('basket:add', addToBasketEventHandler);
    window.addEventListener('basket:remove', removeFromBasketEventHandler);

    return () => {
      window.removeEventListener('basket:add', addToBasketEventHandler);
      window.removeEventListener('basket:remove', removeFromBasketEventHandler);
    };
  }, []);

  if (!isBasketVisible) {
    return null;
  }

  const onBasketSummaryClick = (e) => {
    e.preventDefault();
    openTrolleyMenu();
  };

  const renderDesktopBasket = () => (
    <PrimaryBarDropdown
      href={isLoggedIn ? links.DISPLAY_BASKET : links.DISPLAY_LOGGED_OUT_BASKET}
      label={<BasketSummary basket={basket} />}
      className={classNames(
        'hd-basket',
        { 'hd-basket--empty': !basket.numberOfItems },
      )}
      linkClassName={'hd-basketSummary'}
    >
      <MiniBasket />
    </PrimaryBarDropdown>
  );

  const renderMobileBasket = () => (
    <Link
      to={isLoggedIn ? links.DISPLAY_BASKET : links.DISPLAY_LOGGED_OUT_BASKET}
      className="hd-basketSummary"
      onClick={onBasketSummaryClick}
      title="Trolley"
      role="button"
    >
      <BasketSummary basket={basket} />
    </Link>
  );

  if (!isDesktopBreakpoint) {
    return renderMobileBasket();
  }

  return renderDesktopBasket();
};

Basket.propTypes = {
  basket: PropTypes.shape({
    value: PropTypes.number.isRequired,
    minimumOrderValue: PropTypes.number.isRequired,
    numberOfItems: PropTypes.number.isRequired,
    offerSavings: PropTypes.number.isRequired,
  }).isRequired,
  isBasketVisible: PropTypes.bool.isRequired,
  isDesktopBreakpoint: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool,
  openTrolleyMenu: PropTypes.func.isRequired,
};

export default Basket;
